import React, { useEffect, useMemo, useState } from "react";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import axios from "axios";
import { HiOutlineDocumentPlus, HiOutlinePhoto } from "react-icons/hi2";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import PageHeader from "./ui/PageHeader";

const Blog = () => {
  const baseUrl = import.meta.env.VITE_LOCAL_BASE_URL;
  const apiUrl = "https://api-img.eventplanet.in";
  const uploadEndpoint = "api/auth/upload";
  const [value, setValue] = useState("");
  const [imgUrl, setImgUrl] = useState(null);
  const [blogCat, setBlogCat] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [data, setData] = useState({
    keyword: "",
    meta_description: "",
    title: "",
    catSlug: "",
  });

  const errors = useMemo(
    () => ({
      title: !data.title.trim() ? "Title is required." : "",
      catSlug: !data.catSlug ? "Category is required." : "",
      keyword: !data.keyword.trim() ? "Keyword is required." : "",
      meta_description: !data.meta_description.trim()
        ? "Meta description is required."
        : "",
    }),
    [data],
  );

  const hasErrors = Boolean(
    errors.title || errors.catSlug || errors.keyword || errors.meta_description,
  );

  function uploadAdapter(loader) {
    return {
      upload: () => {
        return new Promise((resolve, reject) => {
          const body = new FormData();
          loader.file.then((file) => {
            body.append("image", file);
            axios
              .post(`${apiUrl}/${uploadEndpoint}`, body, {
                headers: {
                  "Content-Type": "multipart/form-data",
                },
              })
              .then((response) => {
                resolve({ default: response.data.url });
              })
              .catch((error) => {
                reject(error);
              });
          });
        });
      },
    };
  }

  function uploadPlugin(editor) {
    editor.plugins.get("FileRepository").createUploadAdapter = (loader) => {
      return uploadAdapter(loader);
    };
  }

  useEffect(() => {
    const getBlogCat = async () => {
      try {
        const res = await axios.get(`${baseUrl}/api/v1/blog-categories`);
        setBlogCat(res.data.data || []);
      } catch (err) {
        toast.error("Categories could not be loaded.");
      }
    };

    getBlogCat();
  }, [baseUrl]);

  const btnHandler = async (event) => {
    event.preventDefault();
    setSubmitted(true);

    if (hasErrors) {
      toast.error("Please complete the highlighted fields.");
      return;
    }

    try {
      setIsSubmitting(true);
      const formData = new FormData();
      formData.append("title", data.title);
      formData.append("meta_description", data.meta_description);
      formData.append("keyword", data.keyword);
      formData.append("cat_slug", data.catSlug);
      formData.append("description", value);

      if (imgUrl) {
        formData.append("image", imgUrl);
      }

      const res = await axios.post(`${baseUrl}/api/v1/blogs`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      toast.success(res.data.message);
      setData({
        keyword: "",
        meta_description: "",
        title: "",
        catSlug: "",
      });
      setImgUrl(null);
      setValue("");
      setSubmitted(false);
      event.target.reset();
    } catch (err) {
      toast.error("Blog could not be created. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const formHandler = (event) => {
    const { name, value: fieldValue } = event.target;
    setData((currentData) => ({ ...currentData, [name]: fieldValue }));
  };

  const imgHandler = (event) => {
    const file = event.target.files[0];
    setImgUrl(file || null);
  };

  return (
    <section className="page-stack">
      <PageHeader
        eyebrow="Blog Management"
        title="Add Blog"
        description="Create a blog post with structured metadata, category placement, and rich content."
        actions={
          <Link to="/dashboard/blog-list" className="btn btn-soft-secondary">
            View Blogs
          </Link>
        }
      />

      <div className="surface-card form-card">
        <form method="post" onSubmit={btnHandler} noValidate>
          <div className="row g-4">
            <div className="col-12 col-lg-4">
              <label className="upload-dropzone" htmlFor="blog-image">
                <span className="upload-icon">
                  <HiOutlinePhoto aria-hidden="true" />
                </span>
                <strong>{imgUrl ? imgUrl.name : "Upload cover image"}</strong>
                <small>Use a sharp image for table previews and cards.</small>
              </label>
              <input
                type="file"
                name="image"
                id="blog-image"
                className="visually-hidden"
                accept="image/*"
                onChange={imgHandler}
              />
            </div>

            <div className="col-12 col-lg-8">
              <div className="row g-3">
                <div className="col-12">
                  <label className="form-label" htmlFor="blog-title">
                    Title
                  </label>
                  <input
                    type="text"
                    name="title"
                    id="blog-title"
                    placeholder="Enter blog title"
                    className={`form-control form-control-lg ${submitted && errors.title ? "is-invalid" : ""}`}
                    value={data.title}
                    onChange={formHandler}
                  />
                  {submitted && errors.title && (
                    <div className="invalid-feedback">{errors.title}</div>
                  )}
                </div>

                <div className="col-12 col-md-6">
                  <label className="form-label" htmlFor="blogcat">
                    Category
                  </label>
                  <select
                    className={`form-select form-control-lg ${submitted && errors.catSlug ? "is-invalid" : ""}`}
                    id="blogcat"
                    onChange={formHandler}
                    name="catSlug"
                    value={data.catSlug}
                  >
                    <option value="">Select category</option>
                    {blogCat.map((cur) => (
                      <option key={cur.id || cur.slug} value={cur.slug}>
                        {cur.name}
                      </option>
                    ))}
                  </select>
                  {submitted && errors.catSlug && (
                    <div className="invalid-feedback">{errors.catSlug}</div>
                  )}
                </div>

                <div className="col-12 col-md-6">
                  <label className="form-label" htmlFor="blog-keyword">
                    Keyword
                  </label>
                  <input
                    type="text"
                    name="keyword"
                    id="blog-keyword"
                    placeholder="Primary keyword"
                    className={`form-control form-control-lg ${submitted && errors.keyword ? "is-invalid" : ""}`}
                    value={data.keyword}
                    onChange={formHandler}
                  />
                  {submitted && errors.keyword && (
                    <div className="invalid-feedback">{errors.keyword}</div>
                  )}
                </div>

                <div className="col-12">
                  <label className="form-label" htmlFor="blog-meta-description">
                    Meta description
                  </label>
                  <textarea
                    name="meta_description"
                    id="blog-meta-description"
                    placeholder="Write a concise SEO description"
                    className={`form-control ${submitted && errors.meta_description ? "is-invalid" : ""}`}
                    rows="3"
                    value={data.meta_description}
                    onChange={formHandler}
                  />
                  {submitted && errors.meta_description && (
                    <div className="invalid-feedback">
                      {errors.meta_description}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="editor-section">
            <label className="form-label">Content</label>
            <div className="editor-shell">
              <CKEditor
                config={{
                  extraPlugins: [uploadPlugin],
                  mediaEmbed: {
                    previewsInData: true,
                  },
                }}
                editor={ClassicEditor}
                data={value}
                onChange={(event, editor) => {
                  setValue(editor.getData());
                }}
              />
            </div>
          </div>

          <div className="form-actions">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    aria-hidden="true"
                  />
                  Publishing
                </>
              ) : (
                <>
                  <HiOutlineDocumentPlus aria-hidden="true" />
                  Add Blog
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
};

export default Blog;
